<script setup lang="ts">
import { ref, reactive } from 'vue'
import { Button } from '@/components/ui/button'
import { Input, PasswordInput } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useFormValidation } from '@/composables/useFormValidation'
import AuthLayout from '@/components/layout/AuthLayout.vue'
import { validateEmail, validatePassword } from '@/lib/validation'

const router = useRouter()
const { login } = useAuth()

// Form data using reactive
const formData = reactive({
  email: '',
  password: '',
})

const rememberMe = ref(false)
const error = ref('')

// Validation schema
const validationSchema = {
  email: validateEmail,
  password: validatePassword,
}

const {
  submitting,
  errors,
  isValid,
  handleFieldTouch,
  markAllFieldsTouched,
  getInputClass,
  setSubmitting,
} = useFormValidation(validationSchema, formData)

const handleLogin = async () => {
  markAllFieldsTouched()
  if (!isValid.value) return

  setSubmitting(true)
  error.value = ''

  try {
    await login(formData.email.trim(), formData.password, rememberMe.value)
    router.push('/')
  } catch (e: any) {
    // Handle API validation errors with better error messages
    if (e.response?.data?.message) {
      error.value = e.response.data.message
    } else if (e.response?.status === 401) {
      error.value =
        'Invalid email or password. Please check your credentials and try again.'
    } else if (e.response?.status === 422) {
      error.value = 'Please check your email and password format.'
    } else if (e.response?.status >= 500) {
      error.value =
        'Server error, team has been notified. Please try again later.'
    } else if (e.message) {
      error.value = `Login failed: ${e.message}`
    } else {
      error.value =
        'Something went wrong, team has been notified. Please try again later.'
    }
    console.error('Login error:', e)
  } finally {
    setSubmitting(false)
  }
}

const goToRegister = () => {
  router.push('/auth/register')
}
</script>

<template>
  <AuthLayout title="Login details">
    <div class="max-w-md mx-auto space-y-6">
      <div class="flex flex-col items-center mb-8">
        <img
          src="/images/create-account-avatar.png"
          alt="Welcome back"
          class="w-48 h-48 object-contain mb-4"
        />
        <h1 class="text-3xl font-bold text-center">Welcome back!</h1>
      </div>

      <p class="text-gray-700">
        Your information is secure with Maximo. We use the latest in encryption
        and will not share information with any providers without your
        permission.
      </p>

      <form @submit.prevent="handleLogin" class="space-y-4">
        <div class="space-y-2">
          <div class="space-y-1">
            <Input
              type="email"
              placeholder="Your email address"
              v-model="formData.email"
              :class="getInputClass('email')"
              @blur="handleFieldTouch('email')"
            />
            <p v-if="errors.email" class="text-sm text-red-500">
              {{ errors.email }}
            </p>
          </div>

          <div class="space-y-1">
            <PasswordInput
              placeholder="Your password"
              v-model="formData.password"
              :class="getInputClass('password')"
              @blur="handleFieldTouch('password')"
            />
            <p v-if="errors.password" class="text-sm text-red-500">
              {{ errors.password }}
            </p>
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <Checkbox id="remember" v-model="rememberMe" />
            <label for="remember" class="text-sm text-gray-600"
              >Remember me</label
            >
          </div>

          <Button
            variant="link"
            class="text-sm"
            @click="router.push('/auth/forgot-password')"
          >
            Forgot password?
          </Button>
        </div>

        <p v-if="error" class="text-sm text-red-500">
          {{ error }}
        </p>

        <Button
          type="submit"
          size="lg"
          class="w-full"
          :disabled="submitting || !isValid"
        >
          Login
        </Button>
      </form>

      <div class="text-center">
        <p class="text-sm text-gray-600">
          Don't have an account?
          <Button variant="link" class="px-1" @click="goToRegister">
            Sign up here
          </Button>
        </p>
      </div>
    </div>
  </AuthLayout>
</template>
